import { Modal, Badge, Button, TextInput } from "flowbite-react";
import { HiTrash, HiPlus, HiChevronDown, HiSearch } from "react-icons/hi";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { KbIssue, KbSolution } from "@/sharedTypes";
import { useGetKbSolutionsQuery } from "@/api/kbApi/kbSolution";

interface Props {
  open: boolean;
  onClose: () => void;
  issue: KbIssue | null;
  onLinkSolution: (issueId: number, solutionId: number) => void;
  onUnlinkSolution: (issueId: number, solutionId: number) => void;
}

const PAGE_SIZE = 20;
const SCROLL_THRESHOLD_PX = 80;

export default function KbIssueDetailModal({
  open,
  onClose,
  issue,
  onLinkSolution,
  onUnlinkSolution,
}: Props) {
  const { t } = useTranslation(["knowledgeBase"]);
  const [selectedSolutionId, setSelectedSolutionId] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [accumulated, setAccumulated] = useState<KbSolution[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Debounce search to avoid firing a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // When the search term changes, restart pagination so results land on page 1.
  useEffect(() => {
    setPage(1);
    setAccumulated([]);
  }, [debouncedSearch]);

  const { data: solutionsPage, isFetching } = useGetKbSolutionsQuery(
    {
      filters: debouncedSearch ? { search: debouncedSearch } : {},
      pagination: { page, pageSize: PAGE_SIZE },
    },
    { skip: !open },
  );

  // Reset everything when modal closes or issue changes
  useEffect(() => {
    if (!open) {
      setPage(1);
      setAccumulated([]);
      setIsOpen(false);
      setSelectedSolutionId(0);
      setSearchInput("");
      setDebouncedSearch("");
    }
  }, [open, issue?.id]);

  // Focus the search input when the dropdown opens.
  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  // Accumulate pages (dedupe by id). Depend on `open` so reopening the modal
  // re-populates `accumulated` even when solutionsPage comes back from cache
  // with the same reference.
  useEffect(() => {
    if (!open) return;
    const items: KbSolution[] = solutionsPage?.items ?? [];
    if (!items.length) return;
    setAccumulated((prev) => {
      if (page === 1) return items;
      const seen = new Set(prev.map((s) => s.id));
      return [...prev, ...items.filter((s) => !seen.has(s.id))];
    });
  }, [open, solutionsPage, page]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const totalPages: number = solutionsPage?.pagination?.totalPages ?? 1;
  const hasMore = page < totalPages;

  const linkedSolutionIds = useMemo(
    () => new Set(issue?.solutions?.map((s) => s.id) || []),
    [issue?.solutions],
  );
  const availableSolutions = useMemo(
    () => accumulated.filter((s) => !linkedSolutionIds.has(s.id)),
    [accumulated, linkedSolutionIds],
  );

  const selectedSolution = useMemo(
    () => accumulated.find((x) => x.id === selectedSolutionId),
    [accumulated, selectedSolutionId],
  );

  if (!issue) return null;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (!isFetching && hasMore && distanceToBottom < SCROLL_THRESHOLD_PX) {
      setPage((p) => p + 1);
    }
  };

  const handleLink = () => {
    if (selectedSolutionId && issue.id) {
      onLinkSolution(issue.id, selectedSolutionId);
      setSelectedSolutionId(0);
      setIsOpen(false);
    }
  };

  return (
    <Modal show={open} onClose={onClose} size="xl">
      <Modal.Header>{t("knowledgeBase:issue")}</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t("knowledgeBase:issueText")}
            </p>
            <p className="mt-1 text-gray-900 dark:text-white">{issue.kb_issue_text}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("knowledgeBase:category")}
              </p>
              <p className="mt-1">{issue.category?.category_name || "-"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("knowledgeBase:subcategory")}
              </p>
              <p className="mt-1">{issue.subcategory?.subcategory_name || "-"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("knowledgeBase:aiApproved")}
              </p>
              <Badge color={issue.is_ai_approved ? "success" : "gray"} size="sm" className="mt-1 w-fit">
                {issue.is_ai_approved ? t("knowledgeBase:yes") : t("knowledgeBase:no")}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t("knowledgeBase:vectorized")}
              </p>
              <Badge
                color={issue.vectorize_status === "vectorized" ? "info" : "gray"}
                size="sm"
                className="mt-1 w-fit"
              >
                {t(`knowledgeBase:vectorizeStatus.${issue.vectorize_status || "not_vectorized"}`)}
              </Badge>
            </div>
          </div>

          <hr className="dark:border-gray-700" />

          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {t("knowledgeBase:linkedSolutions")} ({issue.solutions?.length || 0})
            </p>

            {issue.solutions && issue.solutions.length > 0 ? (
              <div className="space-y-2">
                {issue.solutions.map((solution) => (
                  <div
                    key={solution.id}
                    className="flex items-start justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="mr-2 flex min-w-0 flex-1 items-start gap-2">
                      <span className="mt-0.5 shrink-0 rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-gray-600 dark:text-gray-200">
                        #{solution.id}
                      </span>
                      <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                        {solution.kb_solution_text}
                      </p>
                    </div>
                    <Button
                      size="xs"
                      color="failure"
                      onClick={() => issue.id && solution.id && onUnlinkSolution(issue.id, solution.id)}
                    >
                      <HiTrash className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t("knowledgeBase:noSolutions")}</p>
            )}

            <div className="mt-4 flex gap-2">
              <div className="relative flex-1">
                <button
                  ref={triggerRef}
                  type="button"
                  onClick={() => setIsOpen((v) => !v)}
                  className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-left text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {selectedSolution ? (
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-gray-600 dark:text-gray-200">
                        #{selectedSolution.id}
                      </span>
                      <span className="truncate">{selectedSolution.kb_solution_text}</span>
                    </span>
                  ) : (
                    <span className="text-gray-500">{t("knowledgeBase:linkSolution")}</span>
                  )}
                  <HiChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                </button>
                {isOpen && (
                  <div
                    ref={dropdownRef}
                    onScroll={handleScroll}
                    className="absolute bottom-full left-0 z-50 mb-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800">
                      <TextInput
                        ref={searchInputRef}
                        icon={HiSearch}
                        sizing="sm"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t("knowledgeBase:searchSolutions")}
                      />
                    </div>
                    {availableSolutions.length === 0 && !isFetching && (
                      <div className="p-3 text-center text-xs text-gray-500">
                        {t("knowledgeBase:noSolutions")}
                      </div>
                    )}
                    {availableSolutions.map((s) => (
                      <div
                        key={s.id}
                        onClick={() => {
                          if (s.id) setSelectedSolutionId(s.id);
                          setIsOpen(false);
                        }}
                        className="flex cursor-pointer items-start gap-2 border-b border-gray-100 px-3 py-2 text-sm text-gray-900 last:border-b-0 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
                        title={s.kb_solution_text ?? undefined}
                      >
                        <span className="mt-0.5 shrink-0 rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-gray-600 dark:text-gray-200">
                          #{s.id}
                        </span>
                        {/* Three lines rather than 80 characters: picking the
                            wrong solution is the failure this list has to
                            prevent, and neighbouring entries often share an
                            opening sentence. */}
                        <span className="line-clamp-3 whitespace-pre-wrap">{s.kb_solution_text}</span>
                      </div>
                    ))}
                    {isFetching && (
                      <div className="p-2 text-center text-xs text-gray-500">
                        {t("knowledgeBase:loading") || "..."}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Button
                color="primary"
                size="sm"
                onClick={handleLink}
                disabled={!selectedSolutionId}
              >
                <HiPlus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
